import { useContext, useMemo } from 'react';
import { Filters, GroupByAttribute, SortOptions, TimeFilter, TimeGranularity } from 'services/data.service';
import { calculateGranularity } from 'hooks/utils/calculateTimeGranularity';
import { formatDate } from 'utils/formatDate';
import { useAppStore } from 'hooks/state/useAppStore';
import { isValid } from 'date-fns';
import { BENCHMARKING_TEMPORAL_TYPE_WEEKLY } from 'components/filter/modules/benchmarking/types';
import { TenantConfigContext } from 'context/TenantConfigContext';

export interface AdditionalDateRange {
  key: string;
  timeFilter: TimeFilter;
  comparisonTimeFilter?: TimeFilter | null;
}

export interface DataRequest {
  timeFilter: TimeFilter | null;
  comparisonTimeFilter: TimeFilter | null;
  creationTimeFilter: TimeFilter | null;
  benchmarkingTimeFilter: TimeFilter | null;
  benchmarkingComparisonTimeFilter: TimeFilter | null;
  filters: Filters;
  sortOptions?: SortOptions;
  groupByAttribute?: GroupByAttribute;
  additionalDateRanges?: AdditionalDateRange[];
  adminView: boolean;
}

export const useDataRequest = (): DataRequest => {
  const {
    brands,
    dateRange,
    creationDateRange,
    benchmarkingDateRange,
    benchmark,
    countries,
    marketplaces,
    categories,
    dateBy,
    groupByAttribute,
    fulfillmentMethods,
    genders,
    ageGroups,
    seasons,
    products,
    colors,
    sizes,
  } = useAppStore();

  const { isAdminView } = useContext(TenantConfigContext);

  // Validate date ranges before granularity calculation
  const validDateRange =
    dateRange?.range?.from && dateRange?.range?.to && isValid(dateRange.range.from) && isValid(dateRange.range.to)
      ? dateRange.range
      : null;
  const validCreationDateRange =
    creationDateRange?.range?.from &&
    creationDateRange?.range?.to &&
    isValid(creationDateRange.range.from) &&
    isValid(creationDateRange.range.to)
      ? creationDateRange.range
      : null;

  const dateRangeGranularity = validDateRange ? calculateGranularity(validDateRange) : null;
  const creationRangeGranularity = validCreationDateRange ? calculateGranularity(validCreationDateRange) : null;

  return useMemo(
    () => ({
      adminView: isAdminView,
      timeFilter:
        validDateRange && dateRangeGranularity
          ? {
              fromDate: formatDate(validDateRange.from!),
              toDate: formatDate(validDateRange.to!),
              granularity: dateRangeGranularity,
            }
          : null,
      comparisonTimeFilter:
        dateRange.comparison?.from &&
        dateRange.comparison?.to &&
        isValid(dateRange.comparison.from) &&
        isValid(dateRange.comparison.to) &&
        dateRangeGranularity
          ? {
              fromDate: formatDate(dateRange.comparison.from),
              toDate: formatDate(dateRange.comparison.to),
              granularity: dateRangeGranularity,
            }
          : null,
      creationTimeFilter:
        validCreationDateRange && creationRangeGranularity
          ? {
              fromDate: formatDate(validCreationDateRange.from!),
              toDate: formatDate(validCreationDateRange.to!),
              granularity: creationRangeGranularity,
            }
          : null,
      benchmarkingTimeFilter:
        benchmarkingDateRange?.range?.from &&
        benchmarkingDateRange.range?.to &&
        isValid(benchmarkingDateRange.range.from) &&
        isValid(benchmarkingDateRange.range.to)
          ? {
              fromDate: formatDate(benchmarkingDateRange.range?.from),
              toDate: formatDate(benchmarkingDateRange.range?.to),
              granularity:
                benchmarkingDateRange.temporalType === BENCHMARKING_TEMPORAL_TYPE_WEEKLY
                  ? TimeGranularity.WEEK
                  : TimeGranularity.MONTH,
            }
          : null,
      benchmarkingComparisonTimeFilter:
        benchmarkingDateRange?.comparison?.from &&
        benchmarkingDateRange.comparison?.to &&
        isValid(benchmarkingDateRange.comparison.from) &&
        isValid(benchmarkingDateRange.comparison.to)
          ? {
              fromDate: formatDate(benchmarkingDateRange.comparison?.from),
              toDate: formatDate(benchmarkingDateRange.comparison?.to),
              granularity:
                benchmarkingDateRange.temporalType === BENCHMARKING_TEMPORAL_TYPE_WEEKLY
                  ? TimeGranularity.WEEK
                  : TimeGranularity.MONTH,
            }
          : null,
      groupByAttribute,
      filters: {
        brands,
        benchmark,
        countries,
        categories,
        genders,
        ageGroups,
        seasons,
        marketplaces,
        fulfillmentMethods,
        colors,
        sizes,
        dateBy,
        productNumbers: products?.map(product => product),
      },
    }),
    [
      brands,
      benchmark,
      countries,
      categories,
      genders,
      ageGroups,
      seasons,
      marketplaces,
      fulfillmentMethods,
      colors,
      sizes,
      dateBy,
      products,
      groupByAttribute,
      validDateRange,
      validCreationDateRange,
      dateRangeGranularity,
      creationRangeGranularity,
      dateRange.comparison,
      benchmarkingDateRange,
      isAdminView,
    ]
  );
};
